/**
 * HeaderSection Component
 *
 * Site header/navigation bar with logo, main navigation links, and social links.
 * Responsive design: stacks vertically on mobile, horizontal layout on desktop.
 *
 * Features:
 * - Centered logo
 * - Left-aligned main navigation links
 * - Right-aligned donate button and social icon
 * - Fixed height of 150px
 * - Accessible keyboard navigation
 */

import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Link,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
} from '@mui/material'
import Grid from '@mui/material/Grid2'
import {
  Facebook as FacebookIcon,
  VolunteerActivism as VolunteerActivismIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material'
import Logo from '@/components/Logo'
import DonateDialog from '@/components/DonateDialog'
import { donationContent } from '@/data'
import { Link as RouterLink, useLocation } from 'react-router-dom'

export interface HeaderSectionProps {
  /** Callback when a navigation link is clicked */
  onNavClick?: (section: string) => void
  /** Callback when the donate button is clicked */
  onDonateClick?: () => void
  /** Callback when a social media icon is clicked */
  onSocialClick?: (platform: string, url: string) => void
  /** Optional CSS class name */
  className?: string
  /** Test ID for testing purposes */
  testId?: string
}

const HeaderSection: React.FC<HeaderSectionProps> = ({
  onNavClick,
  onDonateClick,
  onSocialClick,
  className,
  testId = 'header-section',
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const location = useLocation()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [scrollPosition, setScrollPosition] = useState(0)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null)

  // Check if we're on the home page
  const isHomePage = location.pathname === '/'

  // Track scroll position for logo animation (desktop home page only)
  useEffect(() => {
    if (!isMobile && isHomePage) {
      const handleScroll = () => {
        setScrollPosition(window.scrollY)
      }

      window.addEventListener('scroll', handleScroll)
      return () => window.removeEventListener('scroll', handleScroll)
    }
  }, [isMobile, isHomePage])

  // Calculate logo max width based on scroll position
  // On home page desktop: starts at 230px, transitions to 160px at scroll position 125
  const getLogoMaxWidth = () => {
    if (isMobile || !isHomePage) {
      return '160px'
    }

    if (scrollPosition === 0) {
      return '230px'
    }

    if (scrollPosition >= 125) {
      return '160px'
    }

    // Linear interpolation between 230px and 160px
    const progress = scrollPosition / 125
    const width = 230 - 70 * progress
    return `${width}px`
  }

  const navLinks = [
    { label: 'About', section: 'about', href: '/about' },
    { label: 'Leadership', section: 'leadership', href: '/leadership' },
    {
      label: 'Issues',
      section: 'issues',
      subItems: [
        { label: 'Child Support Reform', href: '/issues/child-support-reform' },
        {
          label: 'Equal Shared Parenting',
          href: '/issues/equal-shared-parenting',
        },
      ],
    },
    {
      label: 'Research',
      section: 'research',
      href: '/research',
      subItems: [
        {
          label: 'Overnights & Parenting Plans',
          href: '/research/overnights-and-parenting-plans',
        },
      ],
    },
    { label: 'Contact', section: 'contact', href: '/contact' },
    { label: 'Events', section: 'events', href: '/events' },
  ]

  const handleNavClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    section: string,
    href?: string
  ) => {
    // If it has an href (route), let React Router handle it
    if (href) {
      // Don't prevent default - let RouterLink handle navigation
      return
    }

    event.preventDefault()
    if (onNavClick) {
      onNavClick(section)
    }
  }

  const handleDonateClick = () => {
    setIsDialogOpen(true)
    if (onDonateClick) {
      onDonateClick()
    }
  }

  const handleDialogClose = () => {
    setIsDialogOpen(false)
  }

  const handleSubmenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    linkLabel: string
  ) => {
    setAnchorEl(event.currentTarget)
    setOpenSubmenu(linkLabel)
  }

  const handleSubmenuClose = () => {
    setAnchorEl(null)
    setOpenSubmenu(null)
  }

  const handleDonation = (optionId: string, amount?: number) => {
    console.log('Donation clicked:', optionId, amount)
    // Phase 2: Implement payment integration
  }

  const handleSocialClick = (
    _event: React.MouseEvent<HTMLAnchorElement>,
    platform: string,
    url: string
  ) => {
    if (onSocialClick) {
      onSocialClick(platform, url)
    }
  }

  const handleMobileMenuOpen = () => {
    setMobileMenuOpen(true)
  }

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false)
  }

  const handleMobileNavClick = (link: {
    label: string
    section: string
    href?: string
  }) => {
    setMobileMenuOpen(false)
    if (link.href) {
      // Let RouterLink handle the navigation
      return
    }
    if (onNavClick) {
      onNavClick(link.section)
    }
  }

  return (
    <Box
      component="header"
      role="banner"
      data-testid={testId}
      className={className}
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        height: { xs: '95px', md: '125px' },
        backgroundColor: 'common.white',
        borderBottom: 1,
        borderColor: 'grey.200',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Container maxWidth="xl" sx={{ height: '100%' }}>
        <Grid
          container
          spacing={2}
          sx={{
            height: '100%',
            alignItems: 'center',
          }}
        >
          {/* Left Navigation - Desktop Only */}
          {!isMobile && (
            <Grid
              size={{ xs: 12, md: 4.5 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              <Box
                component="nav"
                ml={3}
                aria-label="Main navigation"
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: { md: 2, lg: 5, xl: 8 },
                  flexWrap: 'nowrap',
                }}
              >
                {navLinks.map(link => (
                  <Box key={link.section} sx={{ position: 'relative' }}>
                    {link.subItems && link.subItems.length > 0 ? (
                      // Render link with submenu
                      <>
                        <Button
                          onClick={e => handleSubmenuOpen(e, link.label)}
                          endIcon={<KeyboardArrowDownIcon />}
                          aria-label={`${link.label} menu`}
                          aria-haspopup="true"
                          aria-expanded={openSubmenu === link.label}
                          sx={{
                            color: 'text.primary',
                            textDecoration: 'none',
                            fontSize: '0.95rem',
                            fontWeight: 500,
                            textTransform: 'none',
                            minWidth: 'auto',
                            padding: 0,
                            '&:hover': {
                              color: 'primary.main',
                              backgroundColor: 'transparent',
                            },
                          }}
                        >
                          {link.label}
                        </Button>
                        <Menu
                          anchorEl={anchorEl}
                          open={openSubmenu === link.label}
                          onClose={handleSubmenuClose}
                          MenuListProps={{
                            'aria-labelledby': `${link.label}-button`,
                          }}
                        >
                          {link.subItems.map(subItem => (
                            <MenuItem
                              key={subItem.href}
                              component={RouterLink}
                              to={subItem.href}
                              onClick={handleSubmenuClose}
                            >
                              {subItem.label}
                            </MenuItem>
                          ))}
                        </Menu>
                      </>
                    ) : (
                      // Render regular link
                      <Link
                        component={link.href ? RouterLink : 'a'}
                        to={link.href}
                        href={link.href || `#${link.section}`}
                        onClick={e =>
                          handleNavClick(e, link.section, link.href)
                        }
                        aria-label={`Navigate to ${link.label}`}
                        sx={{
                          color:
                            link.href === '/donate'
                              ? 'secondary.main'
                              : location.pathname === link.href
                                ? 'primary.main'
                                : 'text.primary',
                          textDecoration: 'none',
                          fontSize: '0.95rem',
                          fontWeight: link.href === '/donate' ? 600 : 500,
                          whiteSpace: 'nowrap',
                          position: 'relative',
                          '&:hover': {
                            color:
                              link.href === '/donate'
                                ? 'secondary.dark'
                                : 'primary.main',
                            textDecoration: 'underline',
                          },
                          '&:focus': {
                            outline: '2px solid',
                            outlineColor: 'primary.main',
                            outlineOffset: '2px',
                          },
                          // Add accent dot for donate link
                          ...(link.href === '/donate' && {
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              top: -8,
                              right: -8,
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              backgroundColor: 'secondary.main',
                              animation: 'pulse 2s ease-in-out infinite',
                            },
                            '@keyframes pulse': {
                              '0%, 100%': {
                                opacity: 1,
                                transform: 'scale(1)',
                              },
                              '50%': {
                                opacity: 0.5,
                                transform: 'scale(1.2)',
                              },
                            },
                          }),
                        }}
                      >
                        {link.label}
                      </Link>
                    )}
                  </Box>
                ))}
              </Box>
            </Grid>
          )}

          {/* Center Logo */}
          <Grid
            size={{ xs: 12, md: 3 }}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: isMobile ? 'center' : 'flex-start',
              pt: isMobile ? 0 : 1.25,
            }}
          >
            <Box
              sx={{
                position: isMobile ? 'relative' : 'absolute',
                zIndex: 99999,
                top: isMobile ? 'auto' : '5px',
                maxWidth: getLogoMaxWidth(),
                left: isMobile ? 'auto' : '50%',
                transform: isMobile ? 'none' : 'translateX(-50%)',
                transition: 'max-width 0.2s ease-in-out',
              }}
            >
              <Link
                component={RouterLink}
                to="/"
                aria-label="Go to home page"
                sx={{
                  display: 'block',
                  textDecoration: 'none',
                  '&:focus': {
                    outline: 'none',
                  },
                }}
              >
                <Logo
                  size={16}
                  variant="dark"
                  ariaLabel="NCESP Logo - National Council for Equal Shared Parenting"
                />
              </Link>
            </Box>
          </Grid>

          {/* Right Section - Donate Button & Social Icon */}
          {!isMobile && (
            <Grid
              size={{ xs: 12, md: 4.5 }}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <IconButton
                component="a"
                href="https://www.facebook.com/profile.php?id=100066880196187"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on Facebook"
                onClick={e =>
                  handleSocialClick(
                    e,
                    'Facebook',
                    'https://www.facebook.com/profile.php?id=100066880196187'
                  )
                }
                sx={{
                  color: 'primary.main',
                  '&:hover': {
                    color: 'primary.dark',
                    transform: 'scale(1.1)',
                  },
                  '&:focus': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: '2px',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <FacebookIcon />
              </IconButton>
              <Button
                variant="contained"
                color="primary"
                onClick={handleDonateClick}
                aria-label="Donate to NCESP"
                startIcon={<VolunteerActivismIcon />}
                size="small"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:focus': {
                    outline: '2px solid',
                    outlineColor: 'primary.dark',
                    outlineOffset: '2px',
                  },
                }}
              >
                Donate
              </Button>
            </Grid>
          )}

          {/* Mobile Navigation - Same row as logo */}
          {isMobile && (
            <Grid
              size={{ xs: 12 }}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'absolute',
                left: 0,
                right: 0,
                px: 2,
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            >
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleDonateClick}
                  aria-label="Donate to NCESP"
                  startIcon={
                    <VolunteerActivismIcon sx={{ fontSize: '1rem' }} />
                  }
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    px: 1.5,
                    py: 0.5,
                  }}
                >
                  Donate
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <IconButton
                  component="a"
                  href="https://www.facebook.com/profile.php?id=100066880196187"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on Facebook"
                  onClick={e =>
                    handleSocialClick(
                      e,
                      'Facebook',
                      'https://www.facebook.com/profile.php?id=100066880196187'
                    )
                  }
                  size="small"
                  sx={{
                    color: 'primary.main',
                    '&:hover': {
                      color: 'primary.dark',
                    },
                  }}
                >
                  <FacebookIcon />
                </IconButton>
                <IconButton
                  onClick={handleMobileMenuOpen}
                  aria-label="Open navigation menu"
                  size="small"
                  sx={{
                    color: 'primary.main',
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            </Grid>
          )}
        </Grid>
      </Container>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            pt: 2,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            px: 2,
            mb: 2,
          }}
        >
          <IconButton
            onClick={handleMobileMenuClose}
            aria-label="Close navigation menu"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <List>
          {navLinks.map(link => (
            <React.Fragment key={link.section}>
              <ListItem disablePadding>
                <ListItemButton
                  component={link.href ? RouterLink : 'div'}
                  to={link.href}
                  onClick={() => handleMobileNavClick(link)}
                  sx={{
                    py: 1.5,
                  }}
                >
                  <ListItemText
                    primary={link.label}
                    primaryTypographyProps={{
                      fontWeight: 500,
                    }}
                  />
                </ListItemButton>
              </ListItem>
              {/* Render submenu items if they exist */}
              {link.subItems && link.subItems.length > 0 && (
                <List sx={{ pl: 4 }}>
                  {link.subItems.map(subItem => (
                    <ListItem key={subItem.href} disablePadding>
                      <ListItemButton
                        component={RouterLink}
                        to={subItem.href}
                        onClick={handleMobileMenuClose}
                        sx={{
                          py: 1,
                        }}
                      >
                        <ListItemText
                          primary={subItem.label}
                          primaryTypographyProps={{
                            fontSize: '0.9rem',
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </React.Fragment>
          ))}
          {/* Add Donate link at the end for mobile menu */}
          <ListItem disablePadding>
            <ListItemButton
              component={RouterLink}
              to="/donate"
              onClick={handleMobileMenuClose}
              sx={{
                py: 1.5,
              }}
            >
              <ListItemText
                primary="Donate"
                primaryTypographyProps={{
                  fontWeight: 500,
                }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>

      {/* Donate Dialog */}
      <DonateDialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        content={donationContent}
        onDonateClick={handleDonation}
      />
    </Box>
  )
}

export default HeaderSection
